// src/components/reporting/ReviewConversationDetail.js - SuperAdmin view of conversation AI interactions
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { exportToCSV, formatDateForCSV } from '../../utils/csvExport';
import './ConversationDetailReport.css';
import './Reporting.css';

const JsonValue = ({ value }) => {
  if (!value) return <em className="text-gray-400">—</em>;
  try {
    let parsed = JSON.parse(value);
    // Handle double-encoded JSON (stored as a JSON string of a JSON string)
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    const pretty = JSON.stringify(parsed, null, 2);
    return <pre className="json-display">{pretty}</pre>;
  } catch {
    return <span>{value}</span>;
  }
};

JsonValue.propTypes = {
  value: PropTypes.string
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const ReviewConversationDetail = ({ conversationId, onBack, breadcrumbs = [] }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get(
          `/api/v1/reports/conversation/${conversationId}/detail`,
          user
        );
        if (response.success) {
          setData(response.data);
        } else {
          setError('Failed to load conversation detail');
        }
      } catch (err) {
        console.error('Error fetching conversation detail:', err);
        setError('Error loading conversation detail');
      } finally {
        setLoading(false);
      }
    };

    if (conversationId && user) {
      fetchDetail();
    }
  }, [conversationId, user]);

  if (loading) {
    return (
      <div className="conversation-detail-report">
        <div className="report-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>Loading...</h2>
        </div>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-detail-report">
        <div className="report-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>Error</h2>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const handleDownloadCSV = () => {
    if (!data?.interactions?.length) return;
    const columns = [
      { key: 'source', label: 'Source' },
      { key: 'messageOrder', label: 'Order' },
      { key: 'phase', label: 'Phase' },
      { key: 'coachAction', label: 'Coach Action' },
      { key: 'phaseAchieved', label: 'Phase Achieved', format: v => v == null ? '' : v ? 'Yes' : 'No' },
      { key: 'dispositionScore', label: 'Disposition' },
      { key: 'userMessage', label: 'User Message' },
      { key: 'scarlettVoice', label: 'Scarlett (AI/Coaching)', format: (_, row) => row.source === 'roleplay' ? (row.coachingFeedback || '') : (row.aiMessage || '') },
      { key: 'characterMessage', label: 'Character', format: (_, row) => row.source === 'roleplay' ? (row.aiMessage || '') : '' },
      { key: 'aiResponse', label: 'AI Response (raw)' },
      ...(showSystemPrompt ? [{ key: 'systemPrompt', label: 'System Prompt' }] : []),
      { key: 'createdAt', label: 'Timestamp', format: formatDateForCSV }
    ];
    const title = data.conversation?.title || 'conversation';
    const sanitized = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    exportToCSV(data.interactions, columns, `review-${sanitized}-${new Date().toISOString().split('T')[0]}`);
  };

  if (!data) {
    return (
      <div className="conversation-detail-report">
        <div className="report-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>No Data Found</h2>
        </div>
      </div>
    );
  }

  const { conversation, interactions, roleplaySession } = data;

  return (
    <div className="conversation-detail-report">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-nav mb-4">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                {breadcrumb.active ? (
                  <span className="text-gray-900 font-medium">{breadcrumb.label}</span>
                ) : (
                  <button onClick={breadcrumb.onClick} className="text-blue-600 hover:text-blue-800 hover:underline">
                    {breadcrumb.label}
                  </button>
                )}
              </React.Fragment>
            ))
          ) : (
            <span className="text-gray-900 font-medium">Conversation Detail</span>
          )}
        </nav>
      </div>

      {/* Header */}
      <div className="report-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h2>AI Interaction Review</h2>
        <label
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={showSystemPrompt}
            onChange={e => setShowSystemPrompt(e.target.checked)}
          />
          Show System Prompt
        </label>
        <button
          onClick={handleDownloadCSV}
          disabled={!data?.interactions?.length}
          className="back-button"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          title="Download interactions as CSV"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download CSV
        </button>
      </div>

      {/* Conversation Summary Card */}
      <div className="conversation-summary-card">
        <div className="summary-header">
          <div className="summary-title">{conversation.title || 'Untitled Conversation'}</div>
          <div className="summary-meta">
            <span className="summary-date">{formatDateTime(conversation.createdAt)}</span>
            <span className="summary-messages">
              {conversation.messageCount} {conversation.messageCount === 1 ? 'Message' : 'Messages'}
            </span>
          </div>
        </div>
        <div className="summary-details-grid">
          <div className="detail-item">
            <span className="detail-label">Type:</span>
            <span className="detail-value">{conversation.type || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Methodology:</span>
            <span className="detail-value">{conversation.methodology || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Status:</span>
            <span className="detail-value">{conversation.status || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Phase:</span>
            <span className="detail-value">{conversation.currentPhase || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Client:</span>
            <span className="detail-value">{conversation.owner?.displayName || conversation.owner?.email || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Client Email:</span>
            <span className="detail-value">{conversation.owner?.email || '—'}</span>
          </div>
        </div>
      </div>

      {/* Roleplay Session Summary */}
      {roleplaySession && (
        <div className="conversation-summary-card" style={{ marginBottom: '1rem' }}>
          <div className="summary-header">
            <div className="summary-title">Roleplay Session</div>
            <div className="summary-meta">
              <span className="summary-date">{formatDateTime(roleplaySession.startedAt)}</span>
            </div>
          </div>
          <div className="summary-details-grid">
            <div className="detail-item">
              <span className="detail-label">Overall Rating:</span>
              <span className="detail-value">{roleplaySession.overallRating || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Intervention Level:</span>
              <span className="detail-value">{roleplaySession.interventionLevel || '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* AI Interactions Table */}
      <div className="messages-section">
        <h3>AI Interactions ({interactions.length})</h3>
        {interactions.length === 0 ? (
          <div className="no-messages">
            <p>No AI interactions recorded for this conversation.</p>
          </div>
        ) : (
          <div className="messages-table-container">
            <table className="messages-table">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Source</th>
                  <th style={{ width: '40px' }}>#</th>
                  <th style={{ width: '120px' }}>Phase</th>
                  <th style={{ width: '100px' }}>Coach Action</th>
                  <th style={{ width: '60px' }}>Disp.</th>
                  <th>User Message</th>
                  <th>Scarlett (AI/Coaching)</th>
                  <th>Character</th>
                  <th>AI Response (raw)</th>
                  {showSystemPrompt && <th>System Prompt</th>}
                </tr>
              </thead>
              <tbody>
                {interactions.map((interaction, index) => (
                  <tr key={interaction.interactionId || index} className={`message-row${interaction.source === 'roleplay' ? ' roleplay-row' : ''}`}>
                    <td className="message-order">
                      <span className={`source-badge source-badge--${interaction.source || 'coaching'}`}>
                        {interaction.source === 'roleplay' ? 'roleplay' : 'coaching'}
                      </span>
                    </td>
                    <td className="message-order">{interaction.messageOrder ?? index + 1}</td>
                    <td className="message-timestamp" style={{ fontSize: '0.75rem' }}>
                      {interaction.phase || '—'}
                      {interaction.phaseAchieved && <span style={{ marginLeft: '4px', color: '#16a34a' }}>✓</span>}
                    </td>
                    <td className="message-timestamp" style={{ fontSize: '0.75rem' }}>
                      {interaction.coachAction || '—'}
                    </td>
                    <td className="message-order" style={{ fontSize: '0.75rem' }}>
                      {interaction.dispositionScore != null ? interaction.dispositionScore : '—'}
                    </td>
                    <td className="message-content">
                      <div className="content-wrapper">
                        {interaction.userMessage || <em className="text-gray-400">—</em>}
                      </div>
                    </td>
                    <td className="message-content">
                      <div className="content-wrapper">
                        {interaction.source === 'roleplay'
                          ? (interaction.coachingFeedback || <em className="text-gray-400">—</em>)
                          : (interaction.aiMessage || <em className="text-gray-400">—</em>)}
                      </div>
                    </td>
                    <td className="message-content">
                      <div className="content-wrapper">
                        {interaction.source === 'roleplay'
                          ? (interaction.aiMessage || <em className="text-gray-400">—</em>)
                          : <em className="text-gray-400">—</em>}
                      </div>
                    </td>
                    <td className="message-content">
                      <div className="content-wrapper">
                        <JsonValue value={interaction.aiResponse} />
                      </div>
                    </td>
                    {showSystemPrompt && (
                      <td className="message-content">
                        <div className="content-wrapper">
                          {interaction.systemPrompt
                            ? <pre className="json-display">{interaction.systemPrompt}</pre>
                            : <em className="text-gray-400">—</em>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

ReviewConversationDetail.propTypes = {
  conversationId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    active: PropTypes.bool
  }))
};

export default ReviewConversationDetail;
